import React, { useState, useEffect, forwardRef, useRef, useImperativeHandle } from 'react';
import cx from 'classnames';

import ArrowDownSolidIcon from '@icons/ArrowDownSolidIcon';
import ArrowUpSolidIcon from '@icons/ArrowUpSolidIcon';
import CloseIcon from '@icons/CloseIcon';
import RequiredIcon from '@icons/RequiredIcon';
import Typography from '@primitives/Typography';
import { inputStates } from '@primitives/BorderedInput/constants';

import { SelectedOptionType } from '@components/MobileMultiSelect/types';
import { transformSelectedOptions, sortedOptions } from '@components/MobileMultiSelect/utils';
import MobileMultiSelectStyles from '@components/MobileMultiSelect/MobileMultiSelect.module.scss';
import SelectPicker from '@components/SelectPicker';
import { SelectPickerValue, SelectPickerOptionProps } from '@components/SelectPicker/types';
import { AutoCompleteInputVariantType } from '@components/AutoComplete/types';
import { getInputVariantClass, resetPickerOptions } from '@components/AutoComplete/utils';
import styles from '@components/AutoComplete/AutoComplete.module.scss';

import { useOutsideClick } from '@hooks';

import { SingleSelectAutoCompleteProps } from './types';
import LoadingIcon from '@icons/LoadingIcon';

const SingleSelectAutoComplete = forwardRef<HTMLInputElement, SingleSelectAutoCompleteProps>(
    (
        {
            variant = 'borderless',
            options,
            noResultTitle = 'No results found!',
            noResultSubTitle = '',
            noResultContent = null,
            inputLabel = '',
            selectedOption = '',
            placeholder = 'Type here to search',
            updateSelectedOptions,
            updateClearAllCallback,
            enableSelectedOptionSorting = false,
            dropdownIconContainerClass = '',
            onBlur,
            onFocus,
            error,
            errorIcon = null,
            errorHelperTextClasses = '',
            required,
            disabled = false,
            labelClasses = '',
            containerClassName = '',
            isServerSide = false,
            showLoader = false,
            onSearchChange,
            customOptionTemplate,
            mainContainerClassName,
            selectPickerWrapperClass,
            showCloseIconOnlyOnFocus = false,
            allowNoClickIfDisabled = false,
            showFocusOnReset = false,
            removeSelectedValueOnClear = false,
            ...restInputProps
        },
        ref,
    ) => {
        const finalInputStatus = error ? inputStates.error : inputStates.default;
        const [showPicker, setShowPicker] = useState<boolean>(false);
        const [inputValue, setInputValue] = useState<string>('');
        const [pickerOptions, setPickerOptions] = useState<Array<SelectPickerOptionProps>>(options);
        const [selectedValue, setSelectedValue] = useState<SelectPickerValue>();
        const [clearAllState, setClearAllState] = useState<boolean>(false);
        const [inputStatus, setInputStatus] =
            useState<AutoCompleteInputVariantType>(finalInputStatus);
        const [emptyState, setEmptyState] = useState<boolean>(false);

        const autoCompleteRef = useRef(null);
        const inputRef = useRef<HTMLInputElement>(null);

        // Expose the inputRef to the parent component
        useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

        useEffect(() => {
            if (inputStatus === inputStates.active) {
                inputRef?.current?.focus();
            }
        }, [inputStatus]);

        useEffect(() => {
            if (options?.length) {
                setPickerOptions(options);
                setEmptyState(false);
            } else {
                setEmptyState(true);
            }
        }, [options]);

        useEffect(() => {
            if (isServerSide) {
                return;
            }
            if (inputValue?.length) {
                const filteredPickerOptions = pickerOptions.filter(
                    (option) =>
                        option.label
                            ?.toLocaleLowerCase()
                            ?.indexOf(inputValue?.toLocaleLowerCase()) > -1,
                );
                if (!filteredPickerOptions.length) {
                    togglePicker(false);
                    setEmptyState(true);
                } else setEmptyState(false);
            }
        }, [inputValue]);

        /** handle the autocomplete error state update when conditionally updated from client */
        useEffect(() => {
            setInputStatus(error ? inputStates.error : inputStates.default);
        }, [error]);

        useEffect(() => {
            if (!selectedOption?.toString()?.length) {
                if (updateSelectedOptions) updateSelectedOptions('');
                setInputValue('');
                return;
            }

            const selectedPickerOption: SelectPickerOptionProps[] = [];
            options?.forEach((option) => {
                if (
                    option?.value?.toString()?.toLowerCase() ===
                    selectedOption?.toString()?.toLowerCase()
                ) {
                    selectedPickerOption.push(option);
                }
            });

            if (!selectedPickerOption?.length) {
                if (updateSelectedOptions) updateSelectedOptions('');
            } else {
                if (enableSelectedOptionSorting) {
                    const transformedOptions = transformSelectedOptions(selectedPickerOption);
                    const sortedPickerOptions = sortedOptions(options, transformedOptions);

                    if (Array.isArray(sortedPickerOptions)) {
                        setPickerOptions(sortedPickerOptions);
                    }
                }

                setSelectedValue(selectedOption);
                setInputValue(selectedPickerOption?.[0]?.label);
            }
        }, [selectedOption]);

        const togglePicker = (bool: boolean) => {
            setShowPicker(bool);
        };

        const handleWrapperClick = () => {
            if (!showPicker) {
                setInputStatus(inputStates.active);
            } else {
                setInputStatus(inputStates.default);
            }
            togglePicker(!showPicker);
            setClearAllState(false);
        };

        const handleSorting = (selectedOptionsFromPicker: SelectedOptionType[]): void => {
            const sortedPickerOptions = sortedOptions(options, selectedOptionsFromPicker);
            if (Array.isArray(sortedPickerOptions) && enableSelectedOptionSorting) {
                setPickerOptions(sortedPickerOptions);
                setSelectedValue(
                    selectedOptionsFromPicker?.map((option) => option?.value)?.[0] || '',
                );
            }
        };

        const onSelectionChange = (option: SelectPickerOptionProps[] | SelectPickerOptionProps) => {
            const transformedOptions = transformSelectedOptions(option, true);
            const finalResult = transformedOptions[transformedOptions.length - 1];
            setInputValue(finalResult?.label);
            setSelectedValue(finalResult?.value);
            if (updateSelectedOptions && typeof updateSelectedOptions === 'function') {
                updateSelectedOptions(finalResult?.value);
            }
            togglePicker(false);
            handleSorting(transformedOptions);
        };

        const resetOptionsToDisplay = (
            e: React.MouseEvent<SVGElement> | React.TouchEvent<SVGElement>,
        ): void => {
            e?.stopPropagation();
            setSelectedValue('');
            setClearAllState(true);
            setInputValue('');
            setPickerOptions(options);

            if (showFocusOnReset) {
                inputRef.current?.focus();
                inputRef.current?.click();
                setInputStatus(inputStates.active);
            }

            if (updateClearAllCallback) {
                updateClearAllCallback([]);
            }
        };

        const returnClearAllIcon = (): JSX.Element | false => {
            return (
                inputValue?.length > 0 &&
                (showCloseIconOnlyOnFocus ? !disabled && showPicker : true) && (
                    <CloseIcon data-testid="close-icon" onClick={(e) => resetOptionsToDisplay(e)} />
                )
            );
        };

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            if (isServerSide && onSearchChange && typeof onSearchChange === 'function') {
                setInputValue(value);
                onSearchChange(value);
                !showPicker && togglePicker(true);
                return;
            }
            if (value === '') {
                setPickerOptions(resetPickerOptions(options));
                setEmptyState(false);

                if (removeSelectedValueOnClear) {
                    setSelectedValue('');
                    setClearAllState(true);
                    inputRef.current?.focus();
                    inputRef.current?.click();
                    setInputStatus(inputStates.active);
                }

                !showPicker && togglePicker(true);
            } else {
                !showPicker && togglePicker(true);
            }
            setInputValue(value);
        };

        const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
            if (inputStatus === inputStates.active) {
                setInputStatus(inputStates.default);
            }

            /* To pass the current event to the consumer */
            if (onBlur) {
                onBlur(event);
            }
        };

        const handleOnFocus = (event: React.FocusEvent<HTMLInputElement>): void => {
            if (inputStatus === inputStates.default) {
                setInputStatus(inputStates.active);
            }
            /* To pass the current event to the consumer */
            if (onFocus) {
                onFocus(event);
            }
        };

        const getErrorHelperTextMessage = (): JSX.Element | false => {
            return (
                typeof error === 'string' &&
                error !== '' && (
                    <div
                        data-testid="autocomplete-error-help-text-wrapper"
                        className={cx(
                            styles['borderless-autocomplete__helper-text-wrapper'],
                            errorHelperTextClasses,
                        )}
                    >
                        <div
                            className={
                                styles[
                                    'borderless-autocomplete__helper-text-wrapper-text-msgs--icon'
                                ]
                            }
                        >
                            {errorIcon !== null && errorIcon}
                        </div>
                        <Typography
                            as="span"
                            variant="p2"
                            className={
                                styles[
                                    'borderless-autocomplete__helper-text-wrapper-text-msgs--error'
                                ]
                            }
                        >
                            {error}
                        </Typography>
                    </div>
                )
            );
        };

        const returnInputLabel = () => {
            const returnLabelComponent = (): JSX.Element => {
                return (
                    <div
                        role="autocomplete-label"
                        className={cx(
                            styles['autocomplete-input-label'],
                            variant === 'borderless'
                                ? styles['autocomplete-input-label--append-animate']
                                : '',
                            labelClasses,
                        )}
                    >
                        <Typography
                            variant="p3"
                            className={styles['autocomplete-input-label--content']}
                        >
                            {inputLabel}
                        </Typography>
                        {required && (
                            <RequiredIcon
                                className={styles['autocomplete-input-label--required']}
                            />
                        )}
                    </div>
                );
            };

            const returnBorderlessVariantLabel = () => {
                if (
                    variant === 'borderless' &&
                    (inputStatus === inputStates.active || inputValue?.length)
                ) {
                    return returnLabelComponent();
                } else null;
            };

            if (inputLabel !== '') {
                return variant === 'borderless'
                    ? returnBorderlessVariantLabel()
                    : returnLabelComponent();
            }
            return null;
        };

        const emptyStateContent = (): JSX.Element | null => {
            if (showLoader) {
                return (
                    <div
                        data-testid="autocomplete-loader-content"
                        className={styles['autocomplete-empty-state-wrapper']}
                    >
                        <LoadingIcon />
                    </div>
                );
            }

            if (emptyState) {
                return noResultContent !== null ? (
                    <div
                        data-testid="autocomplete-no-result-content"
                        className={styles['autocomplete-empty-state-wrapper']}
                    >
                        {noResultContent}
                    </div>
                ) : (
                    <div className={styles['autocomplete-empty-state-wrapper']}>
                        <Typography
                            data-testid="autocomplete-no-result-title"
                            variant="p2"
                            className={styles['autocomplete-empty-state-wrapper--title']}
                        >
                            {noResultTitle}
                        </Typography>
                        {noResultSubTitle?.length > 0 && (
                            <Typography
                                data-testid="autocomplete-no-result-sub-title"
                                variant="p5"
                                className={styles['autocomplete-empty-state-wrapper--sub-title']}
                            >
                                {noResultSubTitle}
                            </Typography>
                        )}
                    </div>
                );
            }
            return null;
        };

        useOutsideClick(autoCompleteRef, () => {
            if (showPicker) {
                togglePicker(false);
            }
        });

        const ArrowIcon = showPicker ? ArrowUpSolidIcon : ArrowDownSolidIcon;

        const selectPickerOptions = isServerSide ? options : pickerOptions;

        return (
            <div ref={autoCompleteRef} className={cx(styles.container, mainContainerClassName)}>
                {returnInputLabel()}
                <div
                    role="autocomplete"
                    className={cx(
                        styles['autocomplete-wrapper'],
                        variant === 'bordered'
                            ? styles['bordered-wrapper']
                            : styles['borderless-autocomplete'],
                        getInputVariantClass(variant, inputStatus),
                        {
                            [styles['autocomplete-wrapper--disabled']]: disabled,
                        },
                        styles['single-select-autocomplete-wrapper'],
                        containerClassName,
                    )}
                    onClick={handleWrapperClick}
                >
                    <div className={styles['autocomplete-container']}>
                        <div
                            data-testid="autocomplete-chip-container"
                            className={styles['chip-container']}
                        >
                            <input
                                role="autocomplete-input"
                                value={inputValue}
                                placeholder={placeholder}
                                onFocus={handleOnFocus}
                                onBlur={handleOnBlur}
                                onChange={handleInputChange}
                                ref={inputRef}
                                disabled={disabled}
                                {...restInputProps}
                            />
                        </div>
                    </div>
                    <div className={styles['autocomplete-icons-container']}>
                        {returnClearAllIcon()}
                        <div className={dropdownIconContainerClass}>
                            <ArrowIcon width={9} />
                        </div>
                    </div>
                </div>
                {!showLoader &&
                showPicker &&
                selectPickerOptions?.length &&
                (allowNoClickIfDisabled ? !disabled : true) ? (
                    <SelectPicker
                        selectedValue={selectedValue}
                        onSelectionChange={onSelectionChange}
                        options={selectPickerOptions}
                        clearAll={clearAllState}
                        wrapperClasses={cx(
                            styles['autocomplete-picker-wrapper'],
                            selectPickerWrapperClass,
                        )}
                        selectPickerItemClassName={
                            MobileMultiSelectStyles['mobile-multi-select__select-picker-option']
                        }
                        selectAllClass={MobileMultiSelectStyles['mobile-multi-select__select-all']}
                        pickerCheckboxClass={styles['picker-checkbox-wrapper']}
                        multiSelect={false}
                        hideBottomCTAs
                        updateSearch={isServerSide ? '' : inputValue}
                        customOptionTemplate={customOptionTemplate}
                    />
                ) : null}
                {showPicker ? emptyStateContent() : null}
                {getErrorHelperTextMessage()}
            </div>
        );
    },
);

export default SingleSelectAutoComplete;
